<template>
  <BaseModal :open="open" size="m" title-id="login-title" mobile-sheet @close="closeModal">
    <div class="login-shell">
      <button class="close-btn" type="button" aria-label="关闭登录弹窗" @click="closeModal">
        <X :size="19" />
      </button>

      <section class="login-main">
        <header class="login-heading">
          <div class="brand-mark"><Sparkles :size="20" /></div>
          <div>
            <p class="eyebrow">拼豆物语账号</p>
            <h3 id="login-title">登录后继续你的创作</h3>
            <p>保存作品、同步进度，也可下载高清图纸。</p>
          </div>
        </header>
        <div class="login-benefits" aria-label="登录说明">
          <span><ShieldCheck :size="15" /> 无需设置密码</span>
          <span><ShieldCheck :size="15" /> 两种方式任选</span>
        </div>

        <div class="method-tabs" role="tablist" aria-label="登录方式">
          <button
            type="button"
            role="tab"
            :aria-selected="activeMethod === 'email'"
            :class="{ active: activeMethod === 'email' }"
            @click="switchMethod('email')"
          >
            <Mail :size="18" /> 邮箱验证码
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeMethod === 'wechat'"
            :class="{ active: activeMethod === 'wechat' }"
            @click="switchMethod('wechat')"
          >
            <MessageCircle :size="18" /> 公众号登录
          </button>
        </div>

        <form v-if="activeMethod === 'email'" class="email-form" @submit.prevent="submitEmailLogin">
          <div class="form-copy"><h4>邮箱快捷登录</h4><p>未注册邮箱验证后自动创建账号。</p></div>

          <label>
            <span>邮箱地址</span>
            <input
              v-model.trim="email"
              type="email"
              inputmode="email"
              autocomplete="email"
              placeholder="例如 name@example.com"
              @input="formError = ''"
            />
          </label>

          <label>
            <span>验证码</span>
            <div class="code-input-row">
              <input
                v-model.trim="code"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                placeholder="输入 6 位验证码"
                @input="normalizeCode"
              />
              <button class="send-code-btn" type="button" :disabled="emailBusy || countdown > 0" @click="sendCode">
                {{ countdown > 0 ? `${countdown}s 后重发` : '获取验证码' }}
              </button>
            </div>
          </label>

          <p v-if="formError" class="form-message error">{{ formError }}</p>
          <p v-else-if="emailStatus" class="form-message" :class="{ error: emailStatusIsError }">{{ emailStatus }}</p>

          <button class="submit-btn" type="submit" :disabled="emailBusy">
            {{ emailBusy ? '正在处理...' : '登录 / 注册' }}
          </button>
          <p class="agreement">继续即代表你同意平台服务规则和隐私说明</p>
        </form>

        <div v-else class="wechat-login">
          <div class="wechat-grid">
            <div class="qr-panel">
              <img v-if="challenge?.qrCodeUrl" :src="challenge.qrCodeUrl" alt="拼豆物语公众号二维码" />
              <div v-else class="qr-placeholder">正在获取公众号二维码...</div>
            </div>
            <div class="wechat-guide">
              <h4>微信扫码关注</h4>
              <p>关注后，把下面的 5 位验证码发送到公众号。</p>
              <button class="login-code" type="button" :disabled="!challenge?.code" @click="copyLoginCode">
                <strong>{{ challenge?.code || '-----' }}</strong>
                <span><Copy :size="15" /> 复制验证码</span>
              </button>
              <small>{{ challenge?.expiresInSeconds ? formatExpiry(challenge.expiresInSeconds) : '验证码生成后会自动检测登录状态' }}</small>
            </div>
          </div>
          <p class="wechat-status">{{ authStatus || '等待公众号验证。' }}</p>
          <div class="wechat-actions">
            <button type="button" class="primary-small" @click="$emit('create-message-login')">重新生成</button>
            <button type="button" class="secondary-small" @click="$emit('refresh-status')">我已发送，刷新状态</button>
          </div>
        </div>
      </section>
    </div>
  </BaseModal>
</template>

<script setup>
import { Copy, Mail, MessageCircle, ShieldCheck, Sparkles, X } from "@lucide/vue";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import BaseModal from "./overlays/BaseModal.vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  challenge: { type: Object, default: null },
  authStatus: { type: String, default: "" },
  emailStatus: { type: String, default: "" },
  emailBusy: { type: Boolean, default: false },
  emailCodeSentNonce: { type: Number, default: 0 }
});

const emit = defineEmits([
  "close",
  "create-message-login",
  "refresh-status",
  "send-email-code",
  "email-login"
]);

const activeMethod = ref("email");
const email = ref("");
const code = ref("");
const formError = ref("");
const countdown = ref(0);
const emailStatusIsError = computed(() => /未开放|失败|错误|无效|过期|检查|稍后/.test(props.emailStatus || ""));
let timer = null;

watch(() => props.open, (value) => {
  if (value) {
    activeMethod.value = "email";
    formError.value = "";
  } else {
    stopCountdown();
  }
});

watch(() => props.emailCodeSentNonce, (next, previous) => {
  if (next > previous) startCountdown();
});

onBeforeUnmount(stopCountdown);

function switchMethod(method) {
  activeMethod.value = method;
  formError.value = "";
  if (method === "wechat" && !props.challenge?.loginToken) {
    emit("create-message-login");
  }
}

function isEmailValid() {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value);
}

function sendCode() {
  if (!isEmailValid()) {
    formError.value = "请输入正确的邮箱地址";
    return;
  }
  formError.value = "";
  emit("send-email-code", email.value);
}

function normalizeCode() {
  code.value = code.value.replace(/\D/g, "").slice(0, 6);
  formError.value = "";
}

function submitEmailLogin() {
  if (!isEmailValid()) {
    formError.value = "请输入正确的邮箱地址";
    return;
  }
  if (!/^\d{6}$/.test(code.value)) {
    formError.value = "请输入邮件中的 6 位验证码";
    return;
  }
  emit("email-login", { email: email.value, code: code.value });
}

function startCountdown() {
  stopCountdown();
  countdown.value = 60;
  timer = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) stopCountdown();
  }, 1000);
}

function stopCountdown() {
  if (timer) window.clearInterval(timer);
  timer = null;
  if (!props.open) countdown.value = 0;
}

async function copyLoginCode() {
  const value = props.challenge?.code;
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
  } catch (error) {
    // Clipboard permission may be unavailable in embedded browsers; selection remains visible.
  }
}

function closeModal() {
  emit("close");
}

function formatExpiry(value) {
  const seconds = Math.max(0, Number(value) || 0);
  if (seconds >= 86400) return `${Math.ceil(seconds / 86400)} 天内有效`;
  if (seconds >= 3600) return `${Math.ceil(seconds / 3600)} 小时内有效`;
  if (seconds >= 60) return `${Math.ceil(seconds / 60)} 分钟内有效`;
  return `剩余 ${seconds} 秒`;
}
</script>

<style scoped>
.login-shell{position:relative;min-height:0;background:#fff;color:var(--text)}
.close-btn{position:absolute;z-index:3;top:16px;right:16px;width:38px;height:38px;border:1px solid #e7e9f2;border-radius:50%;display:grid;place-items:center;background:rgba(255,255,255,.92);color:#667085;cursor:pointer}
.brand-mark{width:44px;height:44px;flex:none;border-radius:14px;display:grid;place-items:center;background:linear-gradient(145deg,#625bf3,#817aff);color:#fff;box-shadow:0 10px 24px rgba(98,91,243,.2)}
.eyebrow{margin:0 0 10px;font-size:13px;font-weight:900;letter-spacing:.12em;text-transform:uppercase}
.login-main{padding:34px 38px 28px;min-width:0}
.login-heading{display:flex;gap:14px;align-items:flex-start;padding-right:48px;margin-bottom:16px}.login-heading .eyebrow{margin:1px 0 5px;color:#625bf3}.login-heading h3{margin:0;font-size:25px;line-height:1.25;color:#20243a}.login-heading>div>p:last-child{margin:6px 0 0;color:#7c8397;font-size:13px}
.login-benefits{display:flex;gap:18px;margin-bottom:19px;padding:10px 13px;border:1px solid #ecebff;border-radius:12px;background:#f8f7ff;color:#615bd6;font-size:12px;font-weight:800}.login-benefits span{display:flex;align-items:center;gap:6px}
.method-tabs{display:grid;grid-template-columns:1fr 1fr;gap:8px;padding:5px;background:#f3f4f8;border-radius:14px;margin-bottom:22px}
.method-tabs button{height:46px;border:0;border-radius:10px;background:transparent;color:#737a90;font-weight:800;display:flex;gap:8px;align-items:center;justify-content:center;cursor:pointer}
.method-tabs button.active{background:#fff;color:#5751e9;box-shadow:0 4px 14px rgba(41,45,74,.1)}
.form-copy h4,.wechat-guide h4{margin:0 0 5px;font-size:21px;color:#20243a}
.form-copy p,.wechat-guide p{margin:0;color:#7c8397;line-height:1.6}
.email-form{display:grid;gap:16px}
.email-form label>span{display:block;margin-bottom:8px;font-size:14px;font-weight:800;color:#40465b}
.email-form input{box-sizing:border-box;width:100%;height:50px;border:1px solid #dfe2ea;border-radius:12px;padding:0 15px;font-size:16px;color:#20243a;background:#fff;outline:none;transition:.2s}
.email-form input:focus{border-color:#6861f2;box-shadow:0 0 0 4px rgba(104,97,242,.1)}
.code-input-row{display:grid;grid-template-columns:1fr 128px;gap:10px}
.send-code-btn{border:1px solid #d9d7ff;border-radius:12px;background:#f2f1ff;color:#5751e9;font-weight:800;cursor:pointer}
.send-code-btn:disabled,.submit-btn:disabled{opacity:.55;cursor:not-allowed}
.submit-btn{height:52px;border:0;border-radius:13px;background:linear-gradient(135deg,#625bf3,#7770ff);color:#fff;font-size:16px;font-weight:900;box-shadow:0 10px 24px rgba(98,91,243,.22);cursor:pointer}
.form-message{margin:-6px 0 0;padding:10px 12px;border-radius:10px;background:#f0f8f4;color:#2f7d56;font-size:13px;line-height:1.5}.form-message.error{background:#fff2f3;color:#b43e4d}
.agreement{margin:-8px 0 0;text-align:center;color:#9a9fb0;font-size:12px}
.wechat-login{display:grid;gap:15px}.wechat-grid{display:grid;grid-template-columns:154px 1fr;gap:20px;align-items:center}.qr-panel{width:154px;height:154px;border:1px solid #e3e5ed;border-radius:18px;padding:9px;box-sizing:border-box;display:grid;place-items:center;background:#fff}.qr-panel img{width:100%;height:100%;object-fit:contain}.qr-placeholder{text-align:center;color:#868ca0;font-size:13px;line-height:1.6}.login-code{display:flex;align-items:center;justify-content:space-between;width:100%;margin:12px 0 7px;padding:10px 12px;border:1px solid #dad8ff;border-radius:12px;background:#f5f4ff;color:#5d56ed;cursor:pointer}.login-code strong{font-size:26px;letter-spacing:.14em}.login-code span{display:flex;align-items:center;gap:5px;font-size:12px;font-weight:800}.wechat-guide small{color:#9297a8}.wechat-status{margin:0;padding:10px 12px;border-radius:11px;background:#f4f8f6;color:#39745a;font-size:13px;line-height:1.5}.wechat-actions{display:flex;gap:10px}.primary-small,.secondary-small{height:42px;padding:0 18px;border-radius:10px;font-weight:800;cursor:pointer}.primary-small{border:0;background:#625bf3;color:#fff}.secondary-small{border:1px solid #dfe2ea;background:#fff;color:#50566c}
@media(max-width:720px){
  .login-shell{display:block;min-height:0;border-radius:24px 24px 0 0}.login-main{padding:22px 18px calc(18px + env(safe-area-inset-bottom))}.close-btn{top:13px;right:14px}.login-heading{padding-right:38px}.login-heading h3{font-size:21px}.login-heading>div>p:last-child{display:none}.login-benefits{gap:10px;justify-content:space-between}.method-tabs{margin-bottom:18px}.method-tabs button{height:44px;font-size:13px}.form-copy h4{font-size:20px}.form-copy p{font-size:13px}.email-form{gap:14px}.email-form input{height:50px}.code-input-row{grid-template-columns:minmax(0,1fr) 112px}.send-code-btn{font-size:12px}.submit-btn{height:52px}.wechat-grid{grid-template-columns:118px 1fr;gap:13px}.qr-panel{width:118px;height:118px}.wechat-guide h4{font-size:18px}.wechat-guide p{font-size:12px}.login-code{margin:9px 0 5px;padding:8px}.login-code strong{font-size:21px}.login-code span{font-size:0}.login-code span svg{width:17px;height:17px}.wechat-actions{display:grid;grid-template-columns:1fr 1.45fr}.primary-small,.secondary-small{padding:0 8px;font-size:12px}
}
</style>
